import { default as cx } from 'classnames';
import React, {
  ChangeEventHandler,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import Label from './Label';
import baseClassName, {
  bareInputClassName,
  inputIconStartInsetClassNames,
  inputIconStartPaddingClassNames,
  inputPaddingClassNames,
  inputSizeClassNames,
  inputVariantClassNames,
  InputSize,
  InputVariant,
} from './styles';

export { default as CheckboxInput } from './Checkbox';
export { default as BareInput } from './Bare';
export { default as Label } from './Label';
export { default as NumberInput } from './Number';
export { default as RadioInput } from './Radio';

export type { InputVariant } from './styles';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'tabIndex'> {
  /** label for input */
  label?: string;
  labelClassName?: string;
  /** tab order */
  tabIndex?: number;
  /* if true, the input will be focused after mounting */
  initiallyFocused?: boolean;
  /* called when the enter/return key is pressed */
  onEnterPressed?: (value?: string) => void;
  padding?: string;
  controlSize?: InputSize;
  appearance?: 'filled' | 'bare';
  /** Decorative icon shown at the start of the input. Custom padding must leave room for it. */
  iconStart?: ReactNode;
  renderSideElement?: (input: React.RefObject<HTMLInputElement>) => ReactNode;
  /* like onChange, but this one is passed a string instead of the event */
  onValueChange?: (value: string) => void;
  name?: string;
  variant?: InputVariant;
  trimInput?: boolean;
}

export type InputHandle = {
  focus: () => void;
  blur: () => void;
  setValue: (value: string) => void;
  getValue: () => string | undefined;
};

export default forwardRef<InputHandle, InputProps>(function Input(
  {
    label,
    labelClassName,
    className,
    tabIndex,
    value,
    padding,
    controlSize = 'md',
    appearance = 'filled',
    iconStart,
    variant,
    initiallyFocused = false,
    onEnterPressed,
    renderSideElement,
    onChange,
    onValueChange,
    trimInput = true,
    ...props
  }: InputProps,
  forwardedRef,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isBare = appearance === 'bare';
  const iconStartPadding = isBare ? 'pl-7' : inputIconStartPaddingClassNames[controlSize];
  const defaultPadding = iconStart
    ? iconStartPadding
    : isBare
      ? undefined
      : inputPaddingClassNames[controlSize];
  const appliedClassName = cx(
    isBare ? bareInputClassName : baseClassName,
    isBare
      ? controlSize === 'sm'
        ? 'h-7 text-sm leading-normal'
        : 'text-base'
      : inputSizeClassNames[controlSize],
    padding ?? defaultPadding,
    className,
  );

  const variantClass = variant ? inputVariantClassNames[variant] : '';

  useEffect(() => {
    if (initiallyFocused) {
      inputRef.current?.focus();
    }
  }, [initiallyFocused]);

  useImperativeHandle(forwardedRef, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    blur: () => {
      inputRef.current?.blur();
    },
    setValue: (value: string) => {
      inputRef.current!.value = value; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    },
    getValue: () => inputRef.current?.value,
  }));

  return (
    <div>
      {label && (
        <Label className={labelClassName} htmlFor={props.id || label}>
          {label}
        </Label>
      )}
      <div className="inline-block w-full relative">
        {iconStart && (
          <span
            aria-hidden="true"
            className={cx(
              'pointer-events-none absolute inset-y-0 flex items-center text-muted',
              isBare ? 'left-0' : inputIconStartInsetClassNames[controlSize],
              props.disabled && 'opacity-60',
            )}
          >
            {iconStart}
          </span>
        )}
        <input
          ref={inputRef}
          tabIndex={tabIndex}
          value={value}
          onKeyPress={(evt) => {
            props?.onKeyPress?.(evt);
            if (evt.key === 'Enter') {
              const submitValue = trimInput ? inputRef.current?.value.trim() : inputRef.current?.value;
              onEnterPressed?.(submitValue);
            }
          }}
          className={cx(appliedClassName, variantClass)}
          onChange={useCallback<ChangeEventHandler<HTMLInputElement>>(
            (evt) => {
              onChange?.(evt);
              onValueChange?.(evt.target.value);
            },
            [onChange, onValueChange],
          )}
          {...props}
        />

        <div className="absolute right-0 top-0 flex h-full items-center mr-0.5">
          {renderSideElement?.(inputRef)}
        </div>
      </div>
    </div>
  );
});
